import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import { settingsLogger as logger } from '../utils/logger';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

/**
 * Centralized Settings Provider
 * Loads all effective settings ONCE and provides them to all other contexts
 * This prevents duplicate API calls to /api/settings/effective
 */
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load all effective settings once
  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      logger.debug('Loading effective settings...');
      const response = await settingsService.getEffectiveSettings();

      if (response.success && response.data) {
        // Convert array of category settings to a map for easy access
        const settingsMap = {};
        response.data.forEach(categorySettings => {
          settingsMap[categorySettings.category] = categorySettings.settings || {};
        });

        setSettings(settingsMap);
        setLastUpdated(new Date());
        logger.debug('Effective settings loaded successfully', settingsMap);
      } else {
        throw new Error('Failed to load settings');
      }
    } catch (err) {
      logger.error('Error loading effective settings:', err);
      setError(err);
      // Set empty settings object to prevent blocking app
      setSettings({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Get settings for a specific category
  const getCategorySettings = useCallback((category) => {
    if (!settings) return {};
    return settings[category] || {};
  }, [settings]);

  // Get a specific setting value
  const getSetting = useCallback((category, key, defaultValue = null) => {
    const categorySettings = getCategorySettings(category);
    return categorySettings[key] !== undefined ? categorySettings[key] : defaultValue;
  }, [getCategorySettings]);

  // Update settings (invalidate cache and reload)
  const refreshSettings = useCallback(async () => {
    logger.debug('Refreshing settings cache...');
    await loadSettings();
  }, [loadSettings]);

  const value = {
    settings,
    isLoading,
    error,
    lastUpdated,
    getCategorySettings,
    getSetting,
    refreshSettings,
    // Provide all category settings directly for convenience
    kafkaSettings: getCategorySettings('KAFKA'),
    connectionSettings: getCategorySettings('CONNECTION'),
    pipelineSettings: getCategorySettings('PIPELINE'),
    webhookSettings: getCategorySettings('WEBHOOK'),
    securitySettings: getCategorySettings('SECURITY'),
    notificationSettings: getCategorySettings('NOTIFICATION'),
    autoSaveSettings: getCategorySettings('AUTO_SAVE'),
    timezoneSettings: getCategorySettings('TIMEZONE'),
    languageSettings: getCategorySettings('LANGUAGE'),
    sessionSettings: getCategorySettings('SESSION'),
    themeSettings: getCategorySettings('THEME'),
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
